/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import './PluginSelectionAdvancedView.css';

import { List } from '@mui/material';
import { type ReactNode } from 'react';

import { PluginGroupItem } from '../PluginGroupItem/PluginGroupItem';
import { PluginItem } from '../PluginItem/PluginItem';
import type { PluginSelectionAdvancedViewProps } from './types';

export function PluginSelectionAdvancedView( {
	filteredCapabilities,
	onPluginSelectionChange,
	onGroupToggle
}: PluginSelectionAdvancedViewProps ): ReactNode {
	return (
		<List className='plugin-list'>
			{ filteredCapabilities
				.filter( capability => !capability.isFiltered )
				.map( capability => (
					<PluginGroupItem
						key={ 'capability-' + capability.name }
						group={ capability }
						onPluginSelectionChange={ onPluginSelectionChange }
						onGroupToggle={ onGroupToggle }
					>
						{ capability.features
							.filter( feature => !feature.isFiltered )
							.map( feature => {
								// When a feature only consists of a single plugin, we do not render the parent wrapper.
								// See: https://github.com/ckeditor/ckeditor5-builder/issues/80.
								if ( feature.plugins.length === 1 ) {
									const [ plugin ] = feature.plugins;

									return (
										<PluginItem
											key={ 'plugin-' + plugin.className }
											plugin={ plugin }
											onPluginSelectionChange={ onPluginSelectionChange }
											isSingleFeatureChoice={ capability.isSingleFeatureChoice }
										/>
									);
								}

								const plugins = feature.plugins
									.filter( plugin => !plugin.isFiltered )
									.map( plugin => (
										<PluginItem
											key={ 'plugin-' + plugin.className }
											plugin={ plugin }
											onPluginSelectionChange={ onPluginSelectionChange }
											isSingleFeatureChoice={ feature.isSinglePluginChoice }
										/>
									) );

								return (
									<PluginGroupItem
										key={ 'feature-' + feature.name }
										group={ feature }
										onPluginSelectionChange={ onPluginSelectionChange }
										isSingleFeatureChoice={ capability.isSingleFeatureChoice }
										searchable
										onGroupToggle={ onGroupToggle }
									>
										{ ...plugins }
									</PluginGroupItem>
								);
							} ) }
					</PluginGroupItem>
				) ) }
		</List>
	);
}
